import { useFormContext } from 'react-hook-form'
import { useUser } from 'store'
import type { RoleState, ContactRoles } from '../types'
import type { User } from 'models/user.model'

export const useEditPermissions = () => {
  const [user] = useUser()
  
  return {
    user: user as User,
    canEdit: user?.admin || user?.roles?.admin_roles
  }
}

export const useEditModeState = (visibleRoles: [string, boolean | undefined][]) => {
  const { watch } = useFormContext<{ roles: ContactRoles }>()
  const roles = watch('roles')
  
  const allSelected = visibleRoles.length > 0 && visibleRoles.every(([role]) => roles?.[role])
  const hasSelection = visibleRoles.some(([role]) => roles?.[role])
  
  return { roles, allSelected, hasSelection }
}

export const useEditModeHandlers = (visibleRoles: [string, boolean | undefined][]) => {
  const { setValue } = useFormContext<{ roles: ContactRoles }>()
  
  const handleAddAll = () => {
    visibleRoles.forEach(([role]) => {
      setValue(`roles.${role}`, true)
    })
  }

  const handleReset = () => {
    visibleRoles.forEach(([role]) => {
      setValue(`roles.${role}`, false)
    })
  }

  const handleToggleAll = (hasSelection: boolean) => {
    visibleRoles.forEach(([role]) => {
      setValue(`roles.${role}`, !hasSelection)
    })
  }

  return { handleAddAll, handleReset, handleToggleAll }
}

export const useBatchModeState = (categoryRoles: string[], rolesState: Record<string, RoleState>) => {
  const allAdded = categoryRoles.every(role => rolesState[role] === 'add')
  
  return { allAdded }
}

export const useBatchModeHandlers = (
  categoryRoles: string[], 
  onRoleChange: (role: string, state: RoleState) => void
) => {
  const handleAddAll = () => {
    categoryRoles.forEach(role => {
      onRoleChange(role, 'add')
    })
  }

  const handleReset = () => {
    categoryRoles.forEach(role => {
      onRoleChange(role, 'unchanged')
    })
  }

  return { handleAddAll, handleReset }
}